import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import type { PlanTier, VenueRole } from '@gatheredhq/types';
import { isMultiVenuePlan } from '@gatheredhq/types';
import type { Restaurant } from '@gatheredhq/types';
import { fetchVenues } from '@/api/venues';
import { apiFetch, ApiError } from '@/api/client';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import type { ApiResponse } from '@gatheredhq/types';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Present when a Gathered platform admin is acting as this org (act-as). */
export interface ImpersonationInfo {
  adminEmail: string;
  reason: string;
  startedAt: number;
  expiresAt: number;
}

interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  avatarUrl: string | null;
  organizationId: string | null;
  plan: PlanTier;
  onboardingCompleted: boolean;
  role: VenueRole | null;
  activeAddons: string[];
  impersonation: ImpersonationInfo | null;
}

interface MeData {
  user: {
    sub: string;
    email: string;
    organizationId: string | null;
    plan: PlanTier;
    role: VenueRole | null;
    onboardingCompleted: boolean;
    impersonation?: ImpersonationInfo | null;
  };
  profile: {
    id: string;
    organizationId: string | null;
    fullName: string;
    avatarUrl: string | null;
    onboardingCompleted: boolean;
  };
}

interface AuthContextValue {
  user: AuthUser | null;
  venues: Restaurant[];
  loading: boolean;
  venuesLoading: boolean;
  /**
   * The API could not be reached — NOT "you are signed out".
   *
   * Lets the route guard say so instead of redirecting to login, which is what made a
   * gateway timeout during a slow import look like being logged out.
   */
  authUnreachable: boolean;
  isMultiVenue: boolean;
  refetchVenues: () => Promise<void>;
  setVenues: (venues: Restaurant[]) => void;
  /** Immediately marks onboarding as complete in local state */
  completeOnboarding: (organizationId: string, fetchedVenues: Restaurant[]) => void;
  /** Re-fetch user + plan from API */
  refreshPlan: () => Promise<void>;
  /**
   * Merge fields into the cached user straight after a successful write.
   *
   * `user` is an in-memory cache of the profile — Topbar renders `fullName` from it.
   * Without this, saving your name left the header showing the old one until the next
   * heartbeat (up to 4 min). Invalidate-on-write, for the cache we already have.
   */
  patchUser: (patch: Partial<Pick<AuthUser, 'fullName' | 'avatarUrl'>>) => void;
  /** Called by CallbackPage / LoginPage after a successful auth event */
  reloadSession: () => Promise<void>;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Cookie helper (presence flag — readable by marketing site) ───────────────

function setAuthCookie(loggedIn: boolean) {
  const maxAge = loggedIn ? 60 * 60 * 24 * 7 : 0; // 7 days — matches session lifetime
  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host.startsWith('127.');
  // Extract root domain from subdomain (e.g. dashboard.gatheredhq.io → .gatheredhq.io)
  const parts = host.split('.');
  const rootDomain = parts.length >= 2 ? `.${parts.slice(-2).join('.')}` : '';
  const domainPart = !isLocal && rootDomain ? `; domain=${rootDomain}` : '';
  document.cookie = `ghq_logged_in=${loggedIn ? '1' : ''}; path=/; max-age=${maxAge}; SameSite=Lax${domainPart}`;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

/**
 * What a `/api/auth/me` probe actually told us.
 *
 * The distinction is the whole point. A bare `catch { return false }` reported a gateway
 * timeout and an expired session identically, and bootstrap signed the owner out on the
 * first one — so a slow import that made the API return 502 logged them out of a session
 * that was perfectly valid. "The server says you are not signed in" and "we could not
 * reach the server" must never take the same branch.
 */
type SessionProbe = 'ok' | 'unauthenticated' | 'unreachable';

/**
 * Only the server may end a session.
 *
 * 401/403 is the server answering. Everything else — a network failure (`status: 0`), a
 * 5xx, a gateway timeout, a parse error — is us failing to ask, and must leave the
 * session alone. Unknown non-ApiError throws are treated as unreachable for the same
 * reason: a bug in this file is not evidence about the owner's credentials.
 */
function classifyAuthFailure(err: unknown): SessionProbe {
  if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
    return 'unauthenticated';
  }
  return 'unreachable';
}

/**
 * Bootstrap retries an unreachable API a couple of times before showing the offline card.
 *
 * Deliberately SHORT (~1.2s total). This covers a momentary blip — one request landing
 * while the API restarts — and nothing more. A longer outage is not something to hide
 * behind a spinner: the owner gets told the server is unreachable, with a retry button,
 * which is both honest and faster to act on than a page that appears to hang.
 */
const BOOTSTRAP_RETRIES = 3;
const BOOTSTRAP_BACKOFF_MS = [300, 900];

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  // In-flight bootstrap request, shared across StrictMode's double effect run.
  const bootstrapPromiseRef = useRef<Promise<SessionProbe> | null>(null);
  const [venues, setVenues] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [venuesLoading, setVenuesLoading] = useState(false);

  async function fetchVenuesFromApi(): Promise<Restaurant[]> {
    setVenuesLoading(true);
    try {
      return await fetchVenues();
    } catch (err) {
      return [];
    } finally {
      setVenuesLoading(false);
    }
  }

  /**
   * Resolve the server session, and optionally refresh the venue list with it.
   *
   * @param force Bypass the `ghq_logged_in` presence check — used right after
   *   login/OAuth handoff, before the cookie has been set.
   * @param withVenues Also GET /api/venues. **Off by default, deliberately.** The
   *   4-minute heartbeat below calls this purely to keep the session alive, and it used
   *   to drag a full venue-list fetch along with every single ping — a second round
   *   trip, every 4 minutes, per tab, forever, for a list that changes when an owner
   *   adds a location. Venues are loaded on bootstrap and re-loaded when the tab
   *   regains focus (which is when another tab may have changed them), not on a timer.
   */
  const loadFromMe = useCallback(async (
    { force = false, withVenues = false }: { force?: boolean; withVenues?: boolean } = {},
  ): Promise<SessionProbe> => {
    // Skip the API call only when we're certain there's no session:
    // no ghq_logged_in presence flag AND no ghq_sid HttpOnly cookie evidence.
    // ghq_sid is HttpOnly so JS can't read it, but ghq_logged_in is the readable proxy.
    // force=true bypasses this entirely (used right after login/OAuth handoff).
    if (!force && !document.cookie.includes('ghq_logged_in=1')) return 'unauthenticated';
    try {
      const res = await apiFetch<ApiResponse<MeData>>('/api/auth/me');
      if (!res.success || !res.data) return 'unauthenticated';

      const { user: jwtUser, profile } = res.data;

      const authUser: AuthUser = {
        id:                  jwtUser.sub,
        email:               jwtUser.email,
        fullName:            profile.fullName,
        avatarUrl:           profile.avatarUrl,
        organizationId:      profile.organizationId ?? jwtUser.organizationId,
        plan:                jwtUser.plan ?? 'free',
        onboardingCompleted: profile.onboardingCompleted || jwtUser.onboardingCompleted,
        role:                jwtUser.role ?? null,
        activeAddons:        [],
        impersonation:       jwtUser.impersonation ?? null,
      };

      setUser(authUser);
      setAuthCookie(true);

      if (authUser.onboardingCompleted && withVenues) {
        const fetched = await fetchVenuesFromApi();
        setVenues(fetched);
      }

      // Billing is NOT fetched here any more. It used to be a third serial round trip
      // (/auth/me → /venues → /billing/status) that every consumer then duplicated,
      // and the heartbeat repeated the whole chain every 4 minutes. It's now a shared
      // query — see `useBillingStatus` below.
      return 'ok';
    } catch (err) {
      return classifyAuthFailure(err);
    }
  }, []);

  const [authUnreachable, setAuthUnreachable] = useState(false);

  /**
   * Resolve the session on mount, retrying while the API is unreachable.
   *
   * An API restart or a moment of overload takes seconds; bouncing the owner to the login
   * page for it — and making them believe they were signed out — is a far worse failure
   * than a short wait. Stops immediately on a real answer either way.
   */
  const bootstrapSession = useCallback(async (): Promise<SessionProbe> => {
    for (let attempt = 0; ; attempt++) {
      const probe = await loadFromMe({ withVenues: true });
      if (probe !== 'unreachable' || attempt >= BOOTSTRAP_RETRIES - 1) return probe;
      await new Promise(r => setTimeout(r, BOOTSTRAP_BACKOFF_MS[attempt] ?? 3000));
    }
  }, [loadFromMe]);

  // ── Bootstrap: check if there's a valid server session on mount ──────────────
  useEffect(() => {
    // Skip auth loading on recovery/reset pages — prevents redirect to onboarding
    const path = window.location.pathname;
    if (path.startsWith('/auth/reset-password') || path.startsWith('/auth/recovery-callback')) {
      setLoading(false);
      return;
    }

    // StrictMode guard: dev's intentional mount→unmount→remount double-invokes
    // this effect, which used to fire /auth/me + /venues twice on every load.
    // A module-level in-flight promise makes the second run share the first
    // request. Deliberately NOT react-query caching — /api/auth/me is the
    // session lifeline (rolls the Redis TTL, refreshes the Supabase token) and
    // must stay an imperative, uncached call. Guarded here at the EFFECT, not
    // the endpoint.
    if (!bootstrapPromiseRef.current) {
      bootstrapPromiseRef.current = bootstrapSession();
    }
    void bootstrapPromiseRef.current.then((probe) => {
      // ONLY the server ending the session clears it. An unreachable API leaves the
      // cookie and the presence flag intact so a reload — or the heartbeat, once the API
      // is back — picks the session up again. Clearing here is what turned a 502 during
      // a slow import into a logout.
      if (probe === 'unauthenticated') {
        setUser(null);
        setVenues([]);
        setAuthCookie(false);
      }
      setAuthUnreachable(probe === 'unreachable');
    }).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Session heartbeat — keeps the Supabase access token fresh while the tab is open.
   *
   * Pings /api/auth/me every 4 min. This is NOT a data read: SessionGuard rolls the
   * 7-day Redis TTL on every authenticated request and proactively refreshes the access
   * token when under 30 min remain. If this ping stops, sessions quietly die.
   *
   * That's why it stays hand-rolled rather than becoming a `useQuery` with a
   * `refetchInterval`: it's a side effect with a bespoke failure policy (below), and
   * modelling it as a cached read would put session liveness at the mercy of cache
   * settings — one `staleTime` tweak away from logging everybody out. See CLAUDE.md §9.
   */
  useEffect(() => {
    const HEARTBEAT_MS = 4 * 60 * 1000; // 4 minutes
    let consecutiveFailures = 0;

    // `withVenues` off: the timer ping only needs to touch the session. Passing it on
    // the visibility path below is what keeps the list current after time away.
    const tryRefresh = (withVenues = false) => {
      if (document.cookie.includes('ghq_logged_in=1')) {
        void loadFromMe({ withVenues }).then(probe => {
          if (probe === 'ok') {
            consecutiveFailures = 0;
            setAuthUnreachable(false);
            return;
          }

          // The server ANSWERED that this session is over. No amount of retrying changes
          // that, and staying on a dead session just fails every subsequent action.
          if (probe === 'unauthenticated') {
            setUser(null);
            setVenues([]);
            setAuthCookie(false);
            return;
          }

          // Unreachable. Ride it out — an import that saturates the API, a restart, or a
          // laptop waking on a dead network all look like this, and none of them mean the
          // owner signed out. After ~12 minutes of it something is genuinely wrong, but
          // even then keep the session: it is the server's to end, not ours.
          consecutiveFailures++;
          if (consecutiveFailures >= 3) setAuthUnreachable(true);
        });
      }
    };

    const interval = setInterval(() => {
      if (!document.hidden) tryRefresh();
    }, HEARTBEAT_MS);

    // Immediately refresh when tab becomes visible after being hidden
    let lastHidden = 0;
    const handleVisibility = () => {
      if (document.hidden) {
        lastHidden = Date.now();
      } else if (lastHidden && Date.now() - lastHidden > 30 * 1000) {
        // Tab was hidden for 30+ seconds — refresh on return, venues included. Coming
        // back is exactly when the list may have moved on (a location added in another
        // tab), and it's the cheap moment to find out — unlike the 4-minute timer,
        // which fires whether or not anything could have changed.
        consecutiveFailures = 0; // reset failures on fresh visibility
        tryRefresh(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function reloadSession() {
    setLoading(true);
    // force: ghq_logged_in isn't set yet right after OAuth handoff.
    // withVenues: this is a fresh sign-in, so there is no venue list to reuse.
    const probe = await loadFromMe({ force: true, withVenues: true });
    // Same rule as bootstrap: only the server ending the session clears it. An
    // unreachable API here (this runs right after the OAuth handoff) must not throw away
    // a session that was just created.
    if (probe === 'unauthenticated') {
      setUser(null);
      setVenues([]);
      setAuthCookie(false);
    }
    setAuthUnreachable(probe === 'unreachable');
    setLoading(false);
  }

  async function refetchVenues() {
    if (user?.onboardingCompleted) {
      const fetched = await fetchVenuesFromApi();
      setVenues(fetched);
    }
  }

  function completeOnboarding(organizationId: string, fetchedVenues: Restaurant[]) {
    setUser((prev) =>
      prev ? { ...prev, organizationId, onboardingCompleted: true } : prev,
    );
    setVenues(fetchedVenues);
  }

  const patchUser = useCallback((patch: Partial<Pick<AuthUser, 'fullName' | 'avatarUrl'>>) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  /**
   * Plan + add-ons, from the one shared billing query.
   *
   * `enabled` on the session, not on the plan: a free org still has a billing status
   * (the server answers `plan: 'free'`), and gating the fetch on the plan we're trying
   * to learn would be circular.
   */
  const { data: billing, refetch: refetchBilling } = useBillingStatus({ enabled: !!user });

  async function refreshPlan() {
    // Was: its own GET /api/billing/status + setUser. Now there is one cache entry, so
    // refreshing it updates every consumer at once — nav, billing page, plan gates.
    // Errors are swallowed as before: the plan refreshes on the next check.
    try { await refetchBilling(); } catch { /* non-critical */ }
  }

  /**
   * `user` as the app sees it, with plan and add-ons overlaid from billing.
   *
   * `jwtUser.plan` is a copy written into the Redis session and only re-patched when a
   * Stripe webhook SCANs for it, so it can lag. `/api/billing/status` reads the org's
   * real subscription — the same source `refreshPlan()` has always trusted to correct
   * it. Preferring it here just makes that correction continuous instead of on-demand.
   * Until the query lands (or for a signed-out user) the session value stands.
   */
  const exposedUser = useMemo<AuthUser | null>(() => {
    if (!user) return null;
    if (!billing) return user;
    return {
      ...user,
      // `?? user.plan` on purpose: a billing payload that arrives without a plan must
      // never blank out a plan we already know. Downgrading someone to `undefined`
      // would close every plan gate in the app.
      plan: (billing.plan as PlanTier | undefined) ?? user.plan,
      activeAddons: billing.activeAddons,
    };
  }, [user, billing]);

  // Multi-venue: command/enterprise plan OR has multiple venues (via addon_location)
  const isMultiVenue = exposedUser
    ? isMultiVenuePlan(exposedUser.plan) || venues.length > 1
    : false;

  return (
    <AuthContext.Provider
      value={{
        user: exposedUser,
        venues,
        loading,
        venuesLoading,
        authUnreachable,
        isMultiVenue,
        refetchVenues,
        setVenues,
        completeOnboarding,
        refreshPlan,
        patchUser,
        reloadSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
